import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  ClipboardList, Star, Bell, ScreenShare, Moon, CheckCircle2, Plus, Trash2, Upload, RefreshCw,
  ChevronLeft, ChevronRight, Check, AlertTriangle, Mail, PhoneCall, Sun, UserPlus, Users,
} from 'lucide-react';
import { useTenantScope } from '../context/TenantScopeContext';
import { useAuth } from '../context/AuthContext';
import { parseCriticalAssetsCsv } from '../utils/criticalAssetsCsv';
import OnboardingDocumentPanel from '../components/OnboardingDocumentPanel';
import OnboardingDocumentStatus from '../components/OnboardingDocumentStatus';

// Wizard di onboarding del tenant (2026-10-19, API /api/onboarding). Cinque passi, ognuno
// salvato per conto suo (si può chiudere e riprendere): asset business critical dichiarati
// (manuali o CSV), destinatari delle notifiche per tipo di alert con canale e fascia, tool di
// accesso remoto consentiti e su quali asset, fascia diurna/notturna con gli asset autorizzati
// a restare accesi, riepilogo. Il tenant è quello del menu "Viewing"; la pagina Tenants lo
// imposta e apre il wizard appena il tenant viene creato.

type Policy = 'inherit' | 'deny' | 'allow';
type TenantPolicy = 'info' | 'deny' | 'allow';

interface Declaration {
  id: number | null;
  hostname: string;
  ipAddress: string;
  macAddress: string;
  roleDescription: string;
  nightAllowed: boolean;
  remoteAccessPolicy: Policy;
  remoteAccessTools: string[];
  // Stato di aggancio all'inventario (sola lettura, dal backend).
  assetId: number | null;
  assetHostname: string | null;
  assetLastSeenAt: string | null;
  matchedBy: 'mac' | 'hostname' | 'ip' | 'inventory' | null;
}

interface AlertType { key: string; label: string; description: string }
// Persone di riferimento (2026-10-20): destinatari con nome e cognome; "notifyDefault" = riceve
// tutti i tipi di alert, salvo i tipi con una scelta propria (Rule.customContacts). Gli id
// negativi sono righe nuove non ancora salvate.
interface Contact { id: number; firstName: string; lastName: string; role: string; email: string; phone: string; notifyDefault: boolean }
interface Rule {
  alertType: string; customContacts: boolean; contactIds: number[];
  emailDay: boolean; emailNight: boolean; phoneDay: boolean; phoneNight: boolean;
}
interface Tool { key: string; name: string; enabled: boolean }
interface InventoryAsset {
  id: number; hostname: string; ip_address: string | null; mac_address: string | null; business_critical: boolean;
  business_role: string | null; night_allowed: boolean; remote_access_policy: Policy; remote_access_tools: string[];
  remote_access_last_tool: string | null; last_seen_at: string;
}
interface TenantSettings {
  id: number; name: string; contact_email: string | null; timezone: string; day_start: string; day_end: string;
  onboarding_completed_at: string | null; remote_access_default: TenantPolicy; remote_access_default_tools: string[];
}
interface NightRow { id: number; hostname: string; night_allowed: boolean; night_hours: number; nights: number; business_critical: boolean }

const STEPS = [
  { key: 'assets', label: 'Critical assets', icon: Star },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'remote', label: 'Remote access', icon: ScreenShare },
  { key: 'night', label: 'Night-time', icon: Moon },
  { key: 'summary', label: 'Summary', icon: CheckCircle2 },
];

const TIMEZONES = ['Europe/Rome', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Zurich', 'Europe/Vienna', 'UTC', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Asia/Dubai', 'Asia/Singapore'];

function emptyDeclaration(): Declaration {
  return { id: null, hostname: '', ipAddress: '', macAddress: '', roleDescription: '', nightAllowed: false, remoteAccessPolicy: 'inherit', remoteAccessTools: [], assetId: null, assetHostname: null, assetLastSeenAt: null, matchedBy: null };
}

function fromApi(d: any): Declaration {
  return {
    id: d.id, hostname: d.hostname || '', ipAddress: d.ip_address || '', macAddress: d.mac_address || '',
    roleDescription: d.role_description || '', nightAllowed: !!d.night_allowed,
    remoteAccessPolicy: d.remote_access_policy || 'inherit', remoteAccessTools: d.remote_access_tools || [],
    assetId: d.asset_id ?? null, assetHostname: d.asset_hostname ?? null, assetLastSeenAt: d.asset_last_seen_at ?? null,
    matchedBy: d.matched_by ?? null,
  };
}

function splitList(v: string): string[] {
  return v.split(/[,;\n]/).map(s => s.trim()).filter(Boolean);
}

function ToolPicker({ tools, value, onChange, disabled }: { tools: Tool[]; value: string[]; onChange: (v: string[]) => void; disabled?: boolean }) {
  return (
    <div className="flex flex-wrap gap-1">
      {tools.map(t => {
        const on = value.includes(t.key);
        return (
          <button key={t.key} type="button" disabled={disabled}
            onClick={() => onChange(on ? value.filter(k => k !== t.key) : [...value, t.key])}
            className={`px-1.5 py-0.5 rounded text-[11px] border ${on ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'} disabled:opacity-40`}>
            {t.name}
          </button>
        );
      })}
    </div>
  );
}

export default function OnboardingWizardPage() {
  const { selectedTenantId, selectedTenantName } = useTenantScope();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [tenant, setTenant] = useState<TenantSettings | null>(null);
  const [alertTypes, setAlertTypes] = useState<AlertType[]>([]);
  const [declarations, setDeclarations] = useState<Declaration[]>([]);
  // Righe viste dal server: salvando si rimuovono solo quelle, non quelle aggiunte nel frattempo
  // dalla pagina Asset.
  const knownDeclarationIds = useRef<number[]>([]);
  const knownLinked = useRef<Array<{ id: number; name: string }>>([]);
  const rememberLinked = (rows: any[]) => {
    knownLinked.current = rows.filter(d => d.asset_id).map(d => ({ id: d.id, name: d.asset_hostname || d.hostname || d.mac_address || d.ip_address || `#${d.asset_id}` }));
  };
  const [rules, setRules] = useState<Rule[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [inventory, setInventory] = useState<InventoryAsset[]>([]);
  const [night, setNight] = useState<NightRow[] | null>(null);
  const [tenantPolicy, setTenantPolicy] = useState<{ policy: TenantPolicy; tools: string[] }>({ policy: 'deny', tools: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [flash, setFlash] = useState<string | null>(null);
  const [csvText, setCsvText] = useState('');
  const [showCsv, setShowCsv] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    if (!selectedTenantId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const [ob, toolsRes, inv] = await Promise.all([
        axios.get(`/api/onboarding/${selectedTenantId}`),
        axios.get('/api/remote-access/tools'),
        axios.get('/api/assets', { params: { tenantId: selectedTenantId } }),
      ]);
      const t: TenantSettings = ob.data.data.tenant;
      setTenant(t);
      setAlertTypes(ob.data.data.alertTypes);
      setDeclarations(ob.data.data.declarations.map(fromApi));
      knownDeclarationIds.current = ob.data.data.declarations.map((d: any) => d.id);
      rememberLinked(ob.data.data.declarations);
      const saved: any[] = ob.data.data.notificationRules;
      const savedContacts: any[] = ob.data.data.contacts || [];
      setContacts(savedContacts.map(c => ({ id: c.id, firstName: c.first_name, lastName: c.last_name || '', role: c.role || '', email: c.email || '', phone: c.phone || '', notifyDefault: c.notify_default })));
      setRules(ob.data.data.alertTypes.map((at: AlertType) => {
        const r = saved.find(x => x.alert_type === at.key);
        return r
          ? { alertType: at.key, customContacts: !!r.custom_contacts, contactIds: r.contact_ids || [], emailDay: r.email_day, emailNight: r.email_night, phoneDay: r.phone_day, phoneNight: r.phone_night }
          // Default sensato: tutte le persone di default, email di giorno e di notte, telefonata
          // mai (si abilita per tipo).
          : { alertType: at.key, customContacts: false, contactIds: [], emailDay: true, emailNight: at.key !== 'low', phoneDay: false, phoneNight: false };
      }));
      setTools(toolsRes.data.data.filter((x: Tool) => x.enabled));
      setInventory(inv.data.data);
      // Il default "info" è quello di piattaforma per non fare rumore prima dell'onboarding: nel
      // wizard si propone "deny", così l'uso di un tool non autorizzato genera l'alert critical.
      setTenantPolicy({ policy: t.remote_access_default === 'info' ? 'deny' : t.remote_access_default, tools: t.remote_access_default_tools || [] });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while loading the onboarding data');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { setStep(0); load(); }, [selectedTenantId]);

  function notify(text: string) { setFlash(text); setTimeout(() => setFlash(null), 2500); }

  // ---------- step 1: asset critici ----------
  function updateDecl(i: number, patch: Partial<Declaration>) {
    setDeclarations(prev => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  }
  function importCsv(text: string) {
    const { rows, skipped, headerDetected } = parseCriticalAssetsCsv(text);
    if (rows.length === 0) { setError('No usable row found in the CSV (each row needs a hostname, an IP or a MAC address).'); return; }
    setDeclarations(prev => {
      const existingKeys = new Set(prev.flatMap(d => [d.macAddress && `mac:${d.macAddress.toLowerCase().replace(/[^0-9a-f]/g, '')}`, d.hostname && `host:${d.hostname.toLowerCase().split('.')[0]}`].filter(Boolean) as string[]));
      const added: Declaration[] = [];
      for (const r of rows) {
        const keys = [r.macAddress && `mac:${r.macAddress.toLowerCase().replace(/[^0-9a-f]/g, '')}`, r.hostname && `host:${r.hostname.toLowerCase().split('.')[0]}`].filter(Boolean) as string[];
        if (keys.some(k => existingKeys.has(k))) continue;
        keys.forEach(k => existingKeys.add(k));
        added.push({ ...emptyDeclaration(), hostname: r.hostname, ipAddress: r.ipAddress, macAddress: r.macAddress, roleDescription: r.roleDescription, nightAllowed: r.nightAllowed });
      }
      notify(`${added.length} asset(s) imported${rows.length - added.length > 0 ? `, ${rows.length - added.length} already in the list` : ''}${skipped > 0 ? `, ${skipped} empty row(s) skipped` : ''}${headerDetected ? '' : ' (no header: columns read as hostname, IP, MAC, role)'}.`);
      return [...prev, ...added];
    });
    setCsvText(''); setShowCsv(false); setError(null);
  }
  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    f.text().then(importCsv);
    e.target.value = '';
  }
  async function saveDeclarations(): Promise<boolean> {
    if (!selectedTenantId) return false;
    setSaving(true); setError(null); setRowErrors({});
    try {
      const items = declarations.map(d => ({
        id: d.id, hostname: d.hostname || null, ipAddress: d.ipAddress || null, macAddress: d.macAddress || null,
        roleDescription: d.roleDescription || null, nightAllowed: d.nightAllowed,
        remoteAccessPolicy: d.remoteAccessPolicy, remoteAccessTools: d.remoteAccessTools,
      }));
      // Togliere una riga agganciata toglie la marcatura e il monitoraggio all'asset: si chiede prima.
      const removing = knownLinked.current.filter(k => !declarations.some(d => d.id === k.id)).map(k => k.name);
      if (removing.length && !window.confirm(`These assets will no longer be Business Critical, and their reachability monitoring and alert escalation will stop:\n\n${removing.join('\n')}\n\nContinue?`)) return false;
      const res = await axios.put(`/api/onboarding/${selectedTenantId}/critical-assets`, { items, knownIds: knownDeclarationIds.current });
      rememberLinked(res.data.data.declarations);
      knownDeclarationIds.current = res.data.data.declarations.map((d: any) => d.id);
      setDeclarations(res.data.data.declarations.map(fromApi));
      notify(`Saved. ${res.data.data.declarations.filter((d: any) => d.asset_id).length} of ${res.data.data.declarations.length} declared asset(s) already found in the inventory.`);
      return true;
    } catch (err: any) {
      const errs: Array<{ index: number; error: string }> = err.response?.data?.errors || [];
      setRowErrors(Object.fromEntries(errs.map(e => [e.index, e.error])));
      setError(err.response?.data?.error ? `${err.response.data.error}${errs.length ? ' — see the highlighted rows' : ''}` : 'Error while saving the critical assets');
      return false;
    } finally {
      setSaving(false);
    }
  }
  async function rematch() {
    if (!selectedTenantId) return;
    const res = await axios.post(`/api/onboarding/${selectedTenantId}/critical-assets/match`);
    setDeclarations(res.data.data.declarations.map(fromApi));
    knownDeclarationIds.current = res.data.data.declarations.map((d: any) => d.id);
    rememberLinked(res.data.data.declarations);
    notify(res.data.data.matched > 0 ? `${res.data.data.matched} asset(s) matched.` : 'No new match in the inventory yet.');
  }

  // ---------- step 2: notifiche ----------
  function updateRule(key: string, patch: Partial<Rule>) {
    setRules(prev => prev.map(r => (r.alertType === key ? { ...r, ...patch } : r)));
  }
  let nextTempId = -1;
  function addContact() {
    setContacts(prev => [...prev, { id: nextTempId--, firstName: '', lastName: '', role: '', email: '', phone: '', notifyDefault: true }]);
  }
  function updateContact(id: number, patch: Partial<Contact>) {
    setContacts(prev => prev.map(c => (c.id === id ? { ...c, ...patch } : c)));
  }
  function removeContact(id: number) {
    setContacts(prev => prev.filter(c => c.id !== id));
    setRules(prev => prev.map(r => ({ ...r, contactIds: r.contactIds.filter(x => x !== id) })));
  }
  // Destinatari effettivi di una regola, per il riepilogo.
  function recipientsOf(r: Rule): Contact[] {
    return r.customContacts ? contacts.filter(c => r.contactIds.includes(c.id)) : contacts.filter(c => c.notifyDefault);
  }
  async function saveRules(): Promise<boolean> {
    if (!selectedTenantId) return false;
    setSaving(true); setError(null);
    try {
      // Prima le persone (le righe nuove ricevono l'id dal server, nello stesso ordine), poi le
      // regole con gli id rimappati.
      const res = await axios.put(`/api/onboarding/${selectedTenantId}/contacts`, {
        contacts: contacts.map(c => ({ id: c.id > 0 ? c.id : null, firstName: c.firstName, lastName: c.lastName, role: c.role, email: c.email, phone: c.phone, notifyDefault: c.notifyDefault })),
      });
      const savedContacts: any[] = res.data.data.contacts;
      const idMap = new Map<number, number>();
      contacts.forEach((c, i) => { if (savedContacts[i]) idMap.set(c.id, savedContacts[i].id); });
      const remapped = rules.map(r => ({ ...r, contactIds: r.contactIds.map(id => idMap.get(id) ?? id).filter(id => id > 0) }));
      setContacts(savedContacts.map(c => ({ id: c.id, firstName: c.first_name, lastName: c.last_name || '', role: c.role || '', email: c.email || '', phone: c.phone || '', notifyDefault: c.notify_default })));
      setRules(remapped);
      await axios.put(`/api/onboarding/${selectedTenantId}/notification-rules`, { rules: remapped });
      notify('Reference people and notification rules saved.');
      return true;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while saving the notification rules');
      return false;
    } finally {
      setSaving(false);
    }
  }

  // ---------- step 3: accesso remoto ----------
  async function saveRemote(): Promise<boolean> {
    if (!selectedTenantId) return false;
    setSaving(true); setError(null);
    try {
      await axios.put(`/api/remote-access/tenant/${selectedTenantId}`, tenantPolicy);
      const ok = await saveDeclarations();
      if (ok) notify('Remote access policies saved.');
      return ok;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while saving the remote access policy');
      return false;
    } finally {
      setSaving(false);
    }
  }
  async function patchAsset(a: InventoryAsset, patch: Record<string, unknown>) {
    const res = await axios.put(`/api/assets/${a.id}`, patch);
    setInventory(prev => prev.map(x => (x.id === a.id ? { ...x, ...res.data.data } : x)));
  }

  // ---------- step 4: notte ----------
  async function loadNight() {
    if (!selectedTenantId) return;
    const res = await axios.get('/api/assets/night-activity', { params: { tenantId: selectedTenantId, days: 7 } });
    setNight(res.data.data.assets);
  }
  useEffect(() => { if (step === 3) loadNight(); }, [step]);
  async function saveNight(): Promise<boolean> {
    if (!selectedTenantId || !tenant) return false;
    setSaving(true); setError(null);
    try {
      await axios.put(`/api/onboarding/${selectedTenantId}/settings`, { timezone: tenant.timezone, dayStart: tenant.day_start.slice(0, 5), dayEnd: tenant.day_end.slice(0, 5) });
      const ok = await saveDeclarations();
      if (ok) notify('Night-time settings saved.');
      return ok;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while saving the night-time settings');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function complete() {
    if (!selectedTenantId) return;
    setSaving(true);
    try {
      await axios.put(`/api/onboarding/${selectedTenantId}/settings`, { completed: true });
      navigate('/asset-center');
    } finally {
      setSaving(false);
    }
  }

  const saveCurrent = async (): Promise<boolean> => {
    if (step === 0) return saveDeclarations();
    if (step === 1) return saveRules();
    if (step === 2) return saveRemote();
    if (step === 3) return saveNight();
    return true;
  };
  async function next() { if (await saveCurrent()) setStep(s => Math.min(STEPS.length - 1, s + 1)); }

  // Asset in inventario non già coperti da una dichiarazione (per i passi 3 e 4).
  const declaredAssetIds = useMemo(() => new Set(declarations.map(d => d.assetId).filter((x): x is number => x !== null)), [declarations]);
  const otherInventory = inventory.filter(a => !declaredAssetIds.has(a.id));
  const configuredRules = rules.filter(r => recipientsOf(r).length > 0);

  if (!selectedTenantId) {
    return (
      <div className="card p-8 text-center text-sm text-gray-500">
        Select a tenant from the "Viewing" menu, or create one from <Link to="/tenants" className="text-primary-600 hover:underline">Tenants</Link>: the wizard opens right after.
      </div>
    );
  }
  if (loading) return <div className="card p-4 text-gray-400 text-sm">Loading…</div>;
  if (!tenant) return <div className="card p-4 text-sm text-red-600 bg-red-50">{error || 'Tenant not available'}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 inline-flex items-center gap-2"><ClipboardList size={20} className="text-primary-600" /> Tenant Onboarding — {selectedTenantName}</h1>
          <p className="text-xs text-gray-400 mt-0.5">
            {tenant.onboarding_completed_at ? `Completed on ${new Date(tenant.onboarding_completed_at).toLocaleDateString('en-GB')} — every step can be revisited.` : 'Every step is saved on its own: you can leave and come back later.'}
          </p>
        </div>
        {flash && <span className="text-xs text-green-700 bg-green-50 rounded px-2 py-1">{flash}</span>}
      </div>

      <ol className="card p-2 flex items-center gap-1 overflow-x-auto">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <li key={s.key}>
              <button onClick={() => setStep(i)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${
                  i === step ? 'bg-primary-600 text-white' : i < step ? 'text-primary-700 bg-primary-50 hover:bg-primary-100' : 'text-gray-500 hover:bg-gray-50'}`}>
                <span className={`w-4 h-4 rounded-full inline-flex items-center justify-center text-[10px] ${i === step ? 'bg-white/20' : 'bg-gray-100 text-gray-500'}`}>{i < step ? <Check size={10} /> : i + 1}</span>
                <Icon size={13} /> {s.label}
              </button>
            </li>
          );
        })}
      </ol>

      {selectedTenantId && <OnboardingDocumentStatus tenantId={selectedTenantId} refreshKey={step} onOpen={() => setStep(STEPS.length - 1)} isOnSummary={step === STEPS.length - 1} />}

      {error && <div className="card p-3 text-sm text-red-700 bg-red-50 inline-flex items-center gap-2"><AlertTriangle size={14} /> {error}</div>}
      {!isAdmin && <div className="card p-3 text-xs text-amber-700 bg-amber-50">Read-only: an admin role is needed to change the onboarding.</div>}

      {step === 0 && (
        <div className="card p-4 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-medium text-gray-900">Business critical assets</h2>
              <p className="text-xs text-gray-500 max-w-2xl">
                The systems the customer cannot work without: servers, domain controllers, ERP, NAS, firewalls. Enter them now,
                even before the platform sees them: as soon as the inventory discovers a device with the same MAC address
                (or hostname, or IP as a last resort) it is marked <strong>Business Critical</strong>, monitored for
                reachability, and its alerts are escalated. At least one of MAC, hostname or IP per row. The MAC address
                is the only strong identity: hostnames and IPs can be forged on the LAN, so fill the MAC whenever you can.
                This list and the Asset Inventory stay aligned: an asset marked critical there appears here, and removing a row
                here removes the Business Critical mark from the asset.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept=".csv,.txt,.tsv" className="hidden" onChange={onFile} />
              <button onClick={() => fileRef.current?.click()} disabled={!isAdmin} className="btn-secondary inline-flex items-center gap-1 text-xs"><Upload size={13} /> Import CSV</button>
              <button onClick={() => setShowCsv(v => !v)} disabled={!isAdmin} className="btn-secondary inline-flex items-center gap-1 text-xs">Paste</button>
              <button onClick={rematch} className="btn-secondary inline-flex items-center gap-1 text-xs" title="Check the inventory again for the declared assets"><RefreshCw size={13} /> Refresh matches</button>
            </div>
          </div>
          {showCsv && (
            <div className="bg-gray-50 rounded-md p-3 space-y-2">
              <p className="text-xs text-gray-500">Paste rows with columns <code>hostname;ip;mac;role;night</code> (header optional, separator <code>;</code> <code>,</code> or tab; night = yes/no).</p>
              <textarea value={csvText} onChange={e => setCsvText(e.target.value)} rows={5} className="input-field w-full font-mono text-xs"
                placeholder={'hostname;ip;mac;role;night\nSRV-ERP01;10.0.0.10;00:11:22:33:44:55;ERP server;yes\nDC01;10.0.0.11;00:11:22:33:44:66;Domain controller;yes'} />
              <button onClick={() => importCsv(csvText)} disabled={!csvText.trim()} className="btn-primary text-xs">Add rows</button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-export-name="Declared business critical assets">
              <thead>
                <tr>
                  <th className="table-header">Hostname</th><th className="table-header">IP address</th><th className="table-header">MAC address</th>
                  <th className="table-header">Role / description</th><th className="table-header">Inventory</th><th className="table-header"></th>
                </tr>
              </thead>
              <tbody>
                {declarations.map((d, i) => (
                  <tr key={d.id ?? `new-${i}`} className={`border-t border-gray-100 ${rowErrors[i] ? 'bg-red-50' : ''}`}>
                    <td className="table-cell"><input value={d.hostname} disabled={!isAdmin} onChange={e => updateDecl(i, { hostname: e.target.value })} placeholder="SRV-ERP01" className="input-field py-0.5 px-1.5 text-xs w-36" /></td>
                    <td className="table-cell"><input value={d.ipAddress} disabled={!isAdmin} onChange={e => updateDecl(i, { ipAddress: e.target.value })} placeholder="10.0.0.10" className="input-field py-0.5 px-1.5 text-xs w-32 font-mono" /></td>
                    <td className="table-cell"><input value={d.macAddress} disabled={!isAdmin} onChange={e => updateDecl(i, { macAddress: e.target.value })} placeholder="00:11:22:33:44:55" className="input-field py-0.5 px-1.5 text-xs w-40 font-mono" /></td>
                    <td className="table-cell">
                      <input value={d.roleDescription} disabled={!isAdmin} onChange={e => updateDecl(i, { roleDescription: e.target.value })} placeholder="ERP server, domain controller…" className="input-field py-0.5 px-1.5 text-xs w-56" />
                      {rowErrors[i] && <div className="text-[11px] text-red-600 mt-0.5">{rowErrors[i]}</div>}
                    </td>
                    <td className="table-cell">
                      {d.assetId
                        ? <Link to={`/asset-center/${d.assetId}`} className="inline-flex items-center gap-1 badge bg-emerald-50 text-emerald-700" title={`Matched to ${d.assetHostname} by ${d.matchedBy || 'identity'}`}><Check size={11} /> Found by {d.matchedBy || '…'}{d.assetLastSeenAt ? ` · seen ${new Date(d.assetLastSeenAt).toLocaleDateString('en-GB')}` : ''}</Link>
                        : d.id ? <span className="badge bg-gray-100 text-gray-500">Not seen yet</span> : <span className="badge bg-sky-50 text-sky-700">New</span>}
                    </td>
                    <td className="table-cell">
                      <button onClick={() => setDeclarations(prev => prev.filter((_, idx) => idx !== i))} disabled={!isAdmin} className="p-1 text-gray-400 hover:text-red-600" title="Remove"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
                {declarations.length === 0 && <tr><td className="table-cell text-gray-400" colSpan={6}>No business critical asset declared yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <button onClick={() => setDeclarations(prev => [...prev, emptyDeclaration()])} disabled={!isAdmin} className="inline-flex items-center gap-1 text-xs text-primary-700 hover:underline"><Plus size={13} /> Add asset</button>
          {inventory.filter(a => a.business_critical && !declaredAssetIds.has(a.id)).length > 0 && (
            <p className="text-xs text-gray-400">
              {inventory.filter(a => a.business_critical && !declaredAssetIds.has(a.id)).length} asset(s) are already marked Business Critical in the inventory without a declaration here; they stay critical either way.
            </p>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <div className="card p-4 space-y-3">
            <h2 className="font-medium text-gray-900 inline-flex items-center gap-2"><Users size={16} className="text-primary-600" /> Reference people</h2>
            <p className="text-xs text-gray-500 max-w-3xl">
              The customer's people to notify, with first and last name. "Default recipient" means they receive every kind of
              alert; below you can change the recipients of a single kind. Each person needs an email address and/or a phone
              number. Email needs SMTP configured in Platform Settings; phone calls need the voice webhook, otherwise they
              appear in the notification bell as a call to make.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-export-name="Reference people">
                <thead>
                  <tr>
                    <th className="table-header">First name</th><th className="table-header">Last name</th><th className="table-header">Role</th>
                    <th className="table-header"><span className="inline-flex items-center gap-1"><Mail size={12} /> Email</span></th>
                    <th className="table-header"><span className="inline-flex items-center gap-1"><PhoneCall size={12} /> Phone</span></th>
                    <th className="table-header text-center">Default recipient</th><th className="table-header"></th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map(c => (
                    <tr key={c.id} className="border-t border-gray-100">
                      <td className="table-cell"><input value={c.firstName} disabled={!isAdmin} onChange={e => updateContact(c.id, { firstName: e.target.value })} className="input-field py-0.5 px-1.5 text-xs w-32" /></td>
                      <td className="table-cell"><input value={c.lastName} disabled={!isAdmin} onChange={e => updateContact(c.id, { lastName: e.target.value })} className="input-field py-0.5 px-1.5 text-xs w-32" /></td>
                      <td className="table-cell"><input value={c.role} disabled={!isAdmin} onChange={e => updateContact(c.id, { role: e.target.value })} placeholder="IT manager" className="input-field py-0.5 px-1.5 text-xs w-32" /></td>
                      <td className="table-cell"><input type="email" value={c.email} disabled={!isAdmin} onChange={e => updateContact(c.id, { email: e.target.value })} placeholder="name@customer.com" className="input-field py-0.5 px-1.5 text-xs w-48" /></td>
                      <td className="table-cell"><input value={c.phone} disabled={!isAdmin} onChange={e => updateContact(c.id, { phone: e.target.value })} placeholder="+39 333 1234567" className="input-field py-0.5 px-1.5 text-xs w-36" /></td>
                      <td className="table-cell text-center"><input type="checkbox" checked={c.notifyDefault} disabled={!isAdmin} onChange={e => updateContact(c.id, { notifyDefault: e.target.checked })} /></td>
                      <td className="table-cell">{isAdmin && <button onClick={() => removeContact(c.id)} className="p-1 text-gray-400 hover:text-red-600" title="Remove"><Trash2 size={14} /></button>}</td>
                    </tr>
                  ))}
                  {contacts.length === 0 && <tr><td className="table-cell text-gray-400 text-xs" colSpan={7}>No reference person yet: alerts will only be visible in the portal.</td></tr>}
                </tbody>
              </table>
            </div>
            {isAdmin && <button onClick={addContact} className="btn-secondary flex items-center gap-1 text-xs"><UserPlus size={14} /> Add person</button>}
          </div>

          <div className="card p-4 space-y-3">
            <h2 className="font-medium text-gray-900">Who is notified, and how</h2>
            <p className="text-xs text-gray-500 max-w-3xl">
              For each kind of alert: who is notified (the default people, or a choice for that kind only) and whether to use
              email and/or phone by day and/or by night (daytime window set in the Night-time step: {tenant.day_start.slice(0, 5)}–{tenant.day_end.slice(0, 5)} {tenant.timezone}).
              An alert matching several kinds notifies the union of their recipients, once.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-export-name="Notification rules">
                <thead>
                  <tr>
                    <th className="table-header">Alert type</th>
                    <th className="table-header">Recipients</th>
                    <th className="table-header text-center"><Mail size={12} className="inline" /> <Sun size={12} className="inline" /> Day</th>
                    <th className="table-header text-center"><Mail size={12} className="inline" /> <Moon size={12} className="inline" /> Night</th>
                    <th className="table-header text-center"><PhoneCall size={12} className="inline" /> <Sun size={12} className="inline" /> Day</th>
                    <th className="table-header text-center"><PhoneCall size={12} className="inline" /> <Moon size={12} className="inline" /> Night</th>
                  </tr>
                </thead>
                <tbody>
                  {alertTypes.map(at => {
                    const r = rules.find(x => x.alertType === at.key)!;
                    const defaults = contacts.filter(c => c.notifyDefault);
                    return (
                      <tr key={at.key} className="border-t border-gray-100 align-top">
                        <td className="table-cell">
                          <div className="font-medium text-gray-800">{at.label}</div>
                          <div className="text-[11px] text-gray-400 max-w-xs">{at.description}</div>
                        </td>
                        <td className="table-cell">
                          <select value={r.customContacts ? 'custom' : 'default'} disabled={!isAdmin}
                            onChange={e => updateRule(at.key, { customContacts: e.target.value === 'custom', contactIds: e.target.value === 'custom' ? defaults.map(c => c.id) : [] })}
                            className="input-field py-0.5 px-1.5 text-xs">
                            <option value="default">Default people ({defaults.length})</option>
                            <option value="custom">Choose for this kind…</option>
                          </select>
                          {r.customContacts && (
                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                              {contacts.map(c => (
                                <label key={c.id} className="inline-flex items-center gap-1 text-xs text-gray-700">
                                  <input type="checkbox" checked={r.contactIds.includes(c.id)} disabled={!isAdmin}
                                    onChange={e => updateRule(at.key, { contactIds: e.target.checked ? [...r.contactIds, c.id] : r.contactIds.filter(x => x !== c.id) })} />
                                  {c.firstName} {c.lastName}
                                </label>
                              ))}
                              {contacts.length === 0 && <span className="text-xs text-gray-400">Add people above first.</span>}
                            </div>
                          )}
                          {!r.customContacts && defaults.length > 0 && (
                            <div className="mt-1 text-[11px] text-gray-400">{defaults.map(c => `${c.firstName} ${c.lastName}`.trim()).join(', ')}</div>
                          )}
                        </td>
                        <td className="table-cell text-center"><input type="checkbox" checked={r.emailDay} disabled={!isAdmin} onChange={e => updateRule(at.key, { emailDay: e.target.checked })} /></td>
                        <td className="table-cell text-center"><input type="checkbox" checked={r.emailNight} disabled={!isAdmin} onChange={e => updateRule(at.key, { emailNight: e.target.checked })} /></td>
                        <td className="table-cell text-center"><input type="checkbox" checked={r.phoneDay} disabled={!isAdmin} onChange={e => updateRule(at.key, { phoneDay: e.target.checked })} /></td>
                        <td className="table-cell text-center"><input type="checkbox" checked={r.phoneNight} disabled={!isAdmin} onChange={e => updateRule(at.key, { phoneNight: e.target.checked })} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="card p-4 space-y-3">
            <h2 className="font-medium text-gray-900">Remote access tools allowed</h2>
            <p className="text-xs text-gray-500 max-w-3xl">
              Remote control software (TeamViewer, AnyDesk, RustDesk, …) is a favourite entry point for attackers. Choose the
              tools the customer legitimately uses and on which assets. Any other tool seen on an asset raises the critical
              alert <em>Unauthorized remote access tool</em> (from Windows process logs, firewall/proxy domains and EDR
              software inventories).
            </p>
            <div className="flex flex-wrap items-start gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Default for every asset of the tenant</label>
                <select value={tenantPolicy.policy} disabled={!isAdmin} onChange={e => setTenantPolicy(p => ({ ...p, policy: e.target.value as TenantPolicy }))} className="input-field text-sm">
                  <option value="deny">Deny all remote tools (alert on any use)</option>
                  <option value="allow">Allow only the tools selected below</option>
                  <option value="info">Informational only (no alert, just record)</option>
                </select>
              </div>
              {tenantPolicy.policy === 'allow' && (
                <div className="flex-1 min-w-[280px]">
                  <label className="block text-xs text-gray-500 mb-1">Tools allowed on all assets</label>
                  <ToolPicker tools={tools} value={tenantPolicy.tools} disabled={!isAdmin} onChange={v => setTenantPolicy(p => ({ ...p, tools: v }))} />
                </div>
              )}
            </div>
          </div>

          <div className="card p-4 space-y-2">
            <h3 className="font-medium text-sm text-gray-900">Per-asset exceptions — declared critical assets</h3>
            <p className="text-xs text-gray-400">Applied to the asset as soon as it is found in the inventory. "Inherit" uses the tenant default. An <strong>Allow</strong> policy is applied only when the asset is matched by MAC address (a spoofed hostname must not inherit an authorisation); "Deny" always applies.</p>
            <table className="w-full text-sm">
              <thead><tr><th className="table-header">Asset</th><th className="table-header">Policy</th><th className="table-header">Allowed tools</th></tr></thead>
              <tbody>
                {declarations.map((d, i) => (
                  <tr key={d.id ?? `new-${i}`} className="border-t border-gray-100 align-top">
                    <td className="table-cell">
                      <div className="font-medium text-gray-800">{d.hostname || d.ipAddress || d.macAddress}</div>
                      <div className="text-[11px] text-gray-400">{d.roleDescription}</div>
                    </td>
                    <td className="table-cell">
                      <select value={d.remoteAccessPolicy} disabled={!isAdmin} onChange={e => updateDecl(i, { remoteAccessPolicy: e.target.value as Policy, remoteAccessTools: e.target.value === 'allow' ? d.remoteAccessTools : [] })} className="input-field py-0.5 px-1.5 text-xs w-36">
                        <option value="inherit">Inherit</option><option value="deny">Deny</option><option value="allow">Allow selected</option>
                      </select>
                    </td>
                    <td className="table-cell">
                      {d.remoteAccessPolicy === 'allow' ? <ToolPicker tools={tools} value={d.remoteAccessTools} disabled={!isAdmin} onChange={v => updateDecl(i, { remoteAccessTools: v })} /> : <span className="text-gray-300">—</span>}
                      {d.remoteAccessPolicy === 'allow' && !d.macAddress && <div className="text-[11px] text-amber-700 mt-1 inline-flex items-center gap-1"><AlertTriangle size={11} /> No MAC address: the allow policy will not be applied to the matched asset.</div>}
                    </td>
                  </tr>
                ))}
                {declarations.length === 0 && <tr><td className="table-cell text-gray-400" colSpan={3}>No declared asset (previous step).</td></tr>}
              </tbody>
            </table>
          </div>

          {otherInventory.length > 0 && (
            <div className="card p-4 space-y-2">
              <h3 className="font-medium text-sm text-gray-900">Per-asset exceptions — assets already in the inventory ({otherInventory.length})</h3>
              <p className="text-xs text-gray-400">Saved immediately on change. Assets where a remote tool was already observed are listed first.</p>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead><tr><th className="table-header">Asset</th><th className="table-header">Seen tool</th><th className="table-header">Policy</th><th className="table-header">Allowed tools</th></tr></thead>
                  <tbody>
                    {[...otherInventory].sort((a, b) => Number(!!b.remote_access_last_tool) - Number(!!a.remote_access_last_tool) || a.hostname.localeCompare(b.hostname)).map(a => (
                      <tr key={a.id} className="border-t border-gray-100 align-top">
                        <td className="table-cell"><Link to={`/asset-center/${a.id}`} className="font-medium text-primary-600 hover:underline">{a.hostname}</Link><div className="text-[11px] text-gray-400 font-mono">{a.ip_address || ''}</div></td>
                        <td className="table-cell text-xs">{a.remote_access_last_tool ? <span className="badge bg-amber-50 text-amber-700">{tools.find(t => t.key === a.remote_access_last_tool)?.name || a.remote_access_last_tool}</span> : <span className="text-gray-300">—</span>}</td>
                        <td className="table-cell">
                          <select value={a.remote_access_policy} disabled={!isAdmin} onChange={e => patchAsset(a, { remoteAccessPolicy: e.target.value, remoteAccessTools: e.target.value === 'allow' ? a.remote_access_tools : [] })} className="input-field py-0.5 px-1.5 text-xs w-36">
                            <option value="inherit">Inherit</option><option value="deny">Deny</option><option value="allow">Allow selected</option>
                          </select>
                        </td>
                        <td className="table-cell">{a.remote_access_policy === 'allow' ? <ToolPicker tools={tools} value={a.remote_access_tools} disabled={!isAdmin} onChange={v => patchAsset(a, { remoteAccessPolicy: 'allow', remoteAccessTools: v })} /> : <span className="text-gray-300">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="card p-4 space-y-3">
            <h2 className="font-medium text-gray-900">Daytime window and night-time activity</h2>
            <p className="text-xs text-gray-500 max-w-3xl">
              Outside the daytime window the platform treats the network as "at night": notification rules switch to their
              night channels, and devices seen active are listed in Asset Inventory ("Night activity") and in the periodic
              report, unless authorised to stay on. Servers and network gear are normally authorised; workstations usually
              should not be on at night.
            </p>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Time zone</label>
                <select value={tenant.timezone} disabled={!isAdmin} onChange={e => setTenant(t => t && ({ ...t, timezone: e.target.value }))} className="input-field text-sm">
                  {[...new Set([tenant.timezone, ...TIMEZONES])].map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1"><Sun size={12} className="inline" /> Day starts at</label>
                <input type="time" value={tenant.day_start.slice(0, 5)} disabled={!isAdmin} onChange={e => setTenant(t => t && ({ ...t, day_start: e.target.value }))} className="input-field text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1"><Moon size={12} className="inline" /> Night starts at</label>
                <input type="time" value={tenant.day_end.slice(0, 5)} disabled={!isAdmin} onChange={e => setTenant(t => t && ({ ...t, day_end: e.target.value }))} className="input-field text-sm" />
              </div>
            </div>
          </div>

          <div className="card p-4 space-y-2">
            <h3 className="font-medium text-sm text-gray-900">Authorised to stay on at night — declared critical assets</h3>
            <table className="w-full text-sm">
              <thead><tr><th className="table-header">Asset</th><th className="table-header">Role</th><th className="table-header">Stays on at night</th></tr></thead>
              <tbody>
                {declarations.map((d, i) => (
                  <tr key={d.id ?? `new-${i}`} className="border-t border-gray-100">
                    <td className="table-cell font-medium text-gray-800">{d.hostname || d.ipAddress || d.macAddress}</td>
                    <td className="table-cell text-gray-500">{d.roleDescription || '—'}</td>
                    <td className="table-cell"><label className="inline-flex items-center gap-1.5 text-xs"><input type="checkbox" checked={d.nightAllowed} disabled={!isAdmin} onChange={e => updateDecl(i, { nightAllowed: e.target.checked })} /> {d.nightAllowed ? 'Authorised' : 'Expected off'}</label></td>
                  </tr>
                ))}
                {declarations.length === 0 && <tr><td className="table-cell text-gray-400" colSpan={3}>No declared asset (first step).</td></tr>}
              </tbody>
            </table>
          </div>

          {otherInventory.length > 0 && (
            <div className="card p-4 space-y-2">
              <h3 className="font-medium text-sm text-gray-900">Authorised to stay on at night — assets already in the inventory ({otherInventory.length})</h3>
              <p className="text-xs text-gray-400">Saved immediately on change. Assets seen active at night in the last 7 days are listed first, with the count.</p>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead><tr><th className="table-header">Asset</th><th className="table-header">Night activity (7 days)</th><th className="table-header">Stays on at night</th></tr></thead>
                  <tbody>
                    {[...otherInventory].map(a => ({ a, n: night?.find(x => x.id === a.id) })).sort((x, y) => (y.n?.nights || 0) - (x.n?.nights || 0) || x.a.hostname.localeCompare(y.a.hostname)).map(({ a, n }) => (
                      <tr key={a.id} className="border-t border-gray-100">
                        <td className="table-cell"><Link to={`/asset-center/${a.id}`} className="font-medium text-primary-600 hover:underline">{a.hostname}</Link>{a.business_critical && <Star size={11} fill="currentColor" className="inline ml-1 text-amber-500" />}<div className="text-[11px] text-gray-400 font-mono">{a.ip_address || ''}</div></td>
                        <td className="table-cell text-xs">{n ? <span className={`badge ${a.night_allowed ? 'bg-gray-100 text-gray-600' : 'bg-indigo-50 text-indigo-700'}`}>{n.nights} night(s) · {n.night_hours} h</span> : <span className="text-gray-300">—</span>}</td>
                        <td className="table-cell"><label className="inline-flex items-center gap-1.5 text-xs"><input type="checkbox" checked={a.night_allowed} disabled={!isAdmin} onChange={e => patchAsset(a, { nightAllowed: e.target.checked })} /> {a.night_allowed ? 'Authorised' : 'Expected off'}</label></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="card p-4 space-y-4">
          <h2 className="font-medium text-gray-900">Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="rounded-md border border-gray-100 p-3">
              <div className="font-medium text-gray-800 inline-flex items-center gap-1.5"><Star size={14} className="text-amber-500" /> Business critical assets</div>
              <p className="text-gray-600 mt-1">{declarations.length} declared, {declarations.filter(d => d.assetId).length} already found in the inventory, {declarations.filter(d => d.nightAllowed).length} authorised at night.</p>
              {declarations.length === 0 && <p className="text-xs text-amber-700 mt-1">No critical asset declared: reachability monitoring and alert escalation will not apply to anything.</p>}
            </div>
            <div className="rounded-md border border-gray-100 p-3">
              <div className="font-medium text-gray-800 inline-flex items-center gap-1.5"><Bell size={14} className="text-primary-600" /> Notifications</div>
              <p className="text-gray-600 mt-1">{contacts.length} reference people; {configuredRules.length} of {alertTypes.length} alert types have recipients; {configuredRules.filter(r => recipientsOf(r).some(c => c.phone) && (r.phoneDay || r.phoneNight)).length} with phone calls.</p>
              {configuredRules.length === 0 && <p className="text-xs text-amber-700 mt-1">No recipient configured: alerts will only be visible in the portal.</p>}
            </div>
            <div className="rounded-md border border-gray-100 p-3">
              <div className="font-medium text-gray-800 inline-flex items-center gap-1.5"><ScreenShare size={14} className="text-primary-600" /> Remote access</div>
              <p className="text-gray-600 mt-1">
                Tenant default: {tenantPolicy.policy === 'deny' ? 'deny all' : tenantPolicy.policy === 'allow' ? `allow ${tenantPolicy.tools.map(k => tools.find(t => t.key === k)?.name || k).join(', ') || 'nothing'}` : 'informational only'};
                {' '}{declarations.filter(d => d.remoteAccessPolicy !== 'inherit').length + inventory.filter(a => !declaredAssetIds.has(a.id) && a.remote_access_policy !== 'inherit').length} per-asset exception(s).
              </p>
            </div>
            <div className="rounded-md border border-gray-100 p-3">
              <div className="font-medium text-gray-800 inline-flex items-center gap-1.5"><Moon size={14} className="text-indigo-600" /> Night-time</div>
              <p className="text-gray-600 mt-1">Day {tenant.day_start.slice(0, 5)}–{tenant.day_end.slice(0, 5)} ({tenant.timezone}); {inventory.filter(a => a.night_allowed).length} inventory asset(s) authorised to stay on at night.</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">Completing the wizard only records the date; everything stays editable here, in Asset Inventory and in Remote Access Tools.</p>
          <OnboardingDocumentPanel tenantId={selectedTenantId} isAdmin={isAdmin} />
        </div>
      )}

      <div className="flex items-center justify-between">
        <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="btn-secondary inline-flex items-center gap-1 disabled:opacity-40"><ChevronLeft size={15} /> Back</button>
        <div className="flex items-center gap-2">
          {step < STEPS.length - 1 && isAdmin && (
            <button onClick={saveCurrent} disabled={saving} className="btn-secondary">{saving ? 'Saving…' : 'Save'}</button>
          )}
          {step < STEPS.length - 1
            ? <button onClick={isAdmin ? next : () => setStep(s => s + 1)} disabled={saving} className="btn-primary inline-flex items-center gap-1">{isAdmin ? 'Save & continue' : 'Next'} <ChevronRight size={15} /></button>
            : <button onClick={complete} disabled={saving || !isAdmin} className="btn-primary inline-flex items-center gap-1"><CheckCircle2 size={15} /> {tenant.onboarding_completed_at ? 'Mark as completed again' : 'Complete onboarding'}</button>}
        </div>
      </div>
    </div>
  );
}
